/**
 * Deep-link helpers for GG+ membership page.
 * All feature gates land on /settings/account/membership with source + return_to.
 */

export const MEMBERSHIP_PATH = '/settings/account/membership';
export const MEMBERSHIP_DEFAULT_BACK = '/settings/account';
const MEMBERSHIP_BACK_KEY = 'gg_membership_back';
const ACCOUNT_BACK_KEY = 'gg_account_back';

/** Map upsell source tags to in-app back paths when return_to is omitted. */
const MEMBERSHIP_SOURCE_BACK = {
  home: '/home',
};

export function membershipUrl({ source = null, returnTo = null } = {}) {
  const params = new URLSearchParams();
  if (source) params.set('source', source);
  if (returnTo) params.set('return_to', returnTo);
  const qs = params.toString();
  return qs ? `${MEMBERSHIP_PATH}?${qs}` : MEMBERSHIP_PATH;
}

/** Read cached entitlements from localStorage (safe parse). */
export function readEntitlements() {
  try {
    return JSON.parse(localStorage.getItem('entitlements')) || null;
  } catch (e) {
    return null;
  }
}

export function isGgPlusActive(entitlements) {
  return !!(entitlements && entitlements.isPremium);
}

/** Format entitlement period end for display. */
export function formatMembershipDate(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 2000) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Approximate trial end (~30 days) for pre-checkout summary copy. */
export function approximateTrialEndDate(days = 30) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return formatMembershipDate(d);
}

/** Safe internal return_to from query string or location. */
export function readReturnTo(searchOrLocation) {
  const search =
    typeof searchOrLocation === 'string'
      ? searchOrLocation
      : (searchOrLocation && searchOrLocation.search) || '';
  const returnTo = new URLSearchParams(search).get('return_to');
  if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
    return returnTo;
  }
  return null;
}

/** Map ?source= to a back path (e.g. home card → /home). */
export function backTargetFromSource(source) {
  if (!source) return null;
  return MEMBERSHIP_SOURCE_BACK[source] || null;
}

/**
 * Remember where to go when leaving membership (Stripe pollutes browser history).
 */
export function storeMembershipBackTarget(path) {
  if (!path || !path.startsWith('/') || path.startsWith('//')) return;
  try {
    sessionStorage.setItem(MEMBERSHIP_BACK_KEY, path);
  } catch (e) {
    // ignore storage errors
  }
}

/** Remember where account settings back should land (avoids account ↔ membership loop). */
export function storeAccountBackTarget(path) {
  if (!path || !path.startsWith('/') || path.startsWith('//')) return;
  try {
    sessionStorage.setItem(ACCOUNT_BACK_KEY, path);
  } catch (e) {
    // ignore storage errors
  }
}

export function resolveAccountBackTarget() {
  try {
    const stored = sessionStorage.getItem(ACCOUNT_BACK_KEY);
    if (stored && stored.startsWith('/') && !stored.startsWith('//')) {
      return stored;
    }
  } catch (e) {
    // ignore storage errors
  }
  return '/home';
}

/**
 * Resolve membership back destination: return_to → source map → stored in-app path → account hub.
 * Never use history.goBack() on membership — Stripe checkout/portal sits in the stack.
 */
export function resolveMembershipBackTarget(searchOrLocation) {
  const search =
    typeof searchOrLocation === 'string'
      ? searchOrLocation
      : (searchOrLocation && searchOrLocation.search) || '';
  const params = new URLSearchParams(search);

  const returnTo = readReturnTo(searchOrLocation);
  if (returnTo) return returnTo;

  const fromSource = backTargetFromSource(params.get('source'));
  if (fromSource) return fromSource;

  try {
    const stored = sessionStorage.getItem(MEMBERSHIP_BACK_KEY);
    if (stored && stored.startsWith('/') && !stored.startsWith('//')) {
      return stored;
    }
  } catch (e) {
    // ignore storage errors
  }
  return MEMBERSHIP_DEFAULT_BACK;
}

/** Monthly price label from entitlements.plan or fallback. */
export function membershipPriceLabel(entitlements) {
  const cents = entitlements && entitlements.plan && entitlements.plan.priceCents;
  if (cents != null && !Number.isNaN(Number(cents))) {
    return `$${Math.round(Number(cents) / 100)}/month`;
  }
  return null;
}

/** Stripe keeps trialing/active when cancel is scheduled at period end. */
export function isMembershipCancelScheduled(entitlements) {
  const sub = entitlements && entitlements.subscription;
  return !!(sub && sub.cancelAtPeriodEnd);
}

export function isMembershipCanceledOrEnding(entitlements) {
  const level = entitlements && entitlements.level;
  return level === 'canceled' || isMembershipCancelScheduled(entitlements);
}

/** User-facing status line for membership summary cards. */
export function membershipStatusLabel(entitlements) {
  const level = entitlements && entitlements.level;
  if (level === 'past_due') return 'Payment issue';
  if (level === 'canceled') return 'Canceled';
  if (isMembershipCancelScheduled(entitlements)) {
    return level === 'trial' ? 'Trial canceled' : 'Canceling';
  }
  if (level === 'trial') return 'Trial';
  if (level === 'active' || level === 'premium') return 'Active';
  return 'Inactive';
}

/** Label for the period-end row. */
export function membershipPeriodEndLabel(entitlements) {
  if (isMembershipCanceledOrEnding(entitlements)) return 'Access until';
  const level = entitlements && entitlements.level;
  if (level === 'trial') return 'Trial ends';
  if (level === 'active' || level === 'premium') return 'Next billing';
  return 'Ends';
}

/** Short notice when cancel is scheduled or subscription is canceled within period. */
export function membershipCancelNotice(entitlements) {
  const level = entitlements && entitlements.level;
  const periodEnd = formatMembershipDate(
    entitlements && entitlements.subscription && entitlements.subscription.currentPeriodEnd
  );

  if (isMembershipCancelScheduled(entitlements)) {
    if (level === 'trial') {
      return periodEnd
        ? `GG+ won't renew and you won't be charged. You'll keep access until ${periodEnd}.`
        : "GG+ won't renew and you won't be charged during your trial.";
    }
    return periodEnd
      ? `GG+ won't renew or charge again after ${periodEnd}. You'll keep access until then.`
      : "GG+ is scheduled to cancel and won't renew.";
  }

  if (level === 'canceled' && periodEnd) {
    return `GG+ is canceled. You'll keep access until ${periodEnd}, then monthly gifts and gift matching will pause.`;
  }

  return null;
}
